import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, Megaphone, Trash2, Plus, Pencil, Check, X, BellRing } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCities } from "@/context/CitiesContext";
import { toast } from "sonner";

// ----------------------------------------------------------------------------
// Module-level cache (per-city) → at most one /public/sponsors fetch per city.
// ----------------------------------------------------------------------------
const _cache = {};
const _inflight = {};

const fetchSponsors = async (city) => {
  const key = city || "_all";
  if (_cache[key]) return _cache[key];
  if (_inflight[key]) return _inflight[key];
  _inflight[key] = (async () => {
    try {
      _cache[key] = (await api.get("/public/sponsors", { params: city ? { city } : {} })).data;
    } catch {
      _cache[key] = [];
    } finally {
      _inflight[key] = null;
    }
    return _cache[key];
  })();
  return _inflight[key];
};

export const invalidateSponsorsCache = () => {
  Object.keys(_cache).forEach((k) => delete _cache[k]);
  Object.keys(_inflight).forEach((k) => delete _inflight[k]);
};

const daysUntil = (iso) => Math.ceil((new Date(iso + "T00:00:00") - new Date()) / 86400000);

// ----------------------------------------------------------------------------
// Public: sidebar spotlight (weighted-random, pinned per page-load)
// ----------------------------------------------------------------------------
export const SponsorTile = ({ city }) => {
  const [sponsors, setSponsors] = useState([]);
  const pickRef = useRef(null);

  useEffect(() => {
    fetchSponsors(city).then(setSponsors);
  }, [city]);

  if (!sponsors.length) return null;

  if (!pickRef.current) {
    const total = sponsors.reduce((a, s) => a + Math.max(1, s.display_weight), 0);
    let roll = Math.random() * total;
    for (const s of sponsors) {
      roll -= Math.max(1, s.display_weight);
      if (roll <= 0) {
        pickRef.current = s;
        break;
      }
    }
    if (!pickRef.current) pickRef.current = sponsors[0];
  }

  const s = pickRef.current;
  const inner = (
    <div className="bg-ec-surface border border-white/10 p-6 flex flex-col items-center text-center hover:border-ec-volt/50 transition-colors">
      <p className="overline text-zinc-600 mb-4 self-start">Sponsored</p>
      <img src={s.image_data_uri} alt={s.name} className="max-h-24 max-w-full object-contain" />
      <p className="font-heading text-xl tracking-wide mt-4">{s.name}</p>
      {s.link_url && (
        <span className="overline text-ec-volt mt-2 inline-flex items-center gap-1">
          Visit <ExternalLink size={12} />
        </span>
      )}
    </div>
  );

  return (
    <div data-testid="sponsor-tile">
      {s.link_url ? (
        <a href={s.link_url} target="_blank" rel="noreferrer noopener" data-testid={`sponsor-tile-link-${s.id}`}>
          {inner}
        </a>
      ) : (
        inner
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Public: site-wide footer strip (all live sponsors, weight-ordered)
// ----------------------------------------------------------------------------
export const SponsorFooter = ({ city }) => {
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    fetchSponsors(city).then(setSponsors);
  }, [city]);

  if (!sponsors.length) return null;

  return (
    <section className="border-t border-white/10 bg-black/30" data-testid="sponsor-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <p className="overline text-zinc-600 text-center mb-6">Our Sponsors</p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
          {sponsors.map((s) => {
            const logo = (
              <img
                src={s.image_data_uri}
                alt={s.name}
                title={s.name}
                className="max-h-12 max-w-[140px] object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            );
            return (
              <div key={s.id} data-testid={`sponsor-footer-item-${s.id}`}>
                {s.link_url ? (
                  <a href={s.link_url} target="_blank" rel="noreferrer noopener">
                    {logo}
                  </a>
                ) : (
                  logo
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ----------------------------------------------------------------------------
// Admin: CRUD grid + add form + dry-run expiry check
// ----------------------------------------------------------------------------
const inputCls =
  "w-full bg-black/40 border border-white/10 px-3.5 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-ec-volt transition-colors";

const blankForm = { name: "", link_url: "", expires_at: "", display_weight: 3, cities: [], file: null };

// All-cities = empty array. Toggling a city flips it in/out of the list.
function CityPicker({ cities, selected, onChange, testidPrefix }) {
  const all = selected.length === 0;
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-cities`}>
      <button
        type="button"
        data-testid={`${testidPrefix}-city-all`}
        onClick={() => onChange([])}
        className={`overline px-3 py-1.5 border transition-colors ${all ? "bg-ec-volt text-black border-ec-volt" : "border-white/15 text-zinc-400"}`}
      >
        All cities
      </button>
      {cities.map((c) => {
        const on = selected.includes(c.slug);
        return (
          <button
            key={c.slug}
            type="button"
            data-testid={`${testidPrefix}-city-${c.slug}`}
            onClick={() => onChange(on ? selected.filter((s) => s !== c.slug) : [...selected, c.slug])}
            className={`overline px-3 py-1.5 border transition-colors ${on ? "bg-ec-volt text-black border-ec-volt" : "border-white/15 text-zinc-400"}`}
          >
            {c.name}
          </button>
        );
      })}
    </div>
  );
}

export const SponsorsAdmin = () => {
  const { cities } = useCities();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/admin/sponsors")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.expires_at || !form.file) {
      toast.error("Name, expiry date and a logo file are required");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("expires_at", form.expires_at);
      fd.append("display_weight", String(form.display_weight));
      if (form.link_url) fd.append("link_url", form.link_url);
      fd.append("cities", form.cities.join(","));
      fd.append("file", form.file);
      await api.post("/admin/sponsors", fd);
      toast.success("Sponsor added");
      setForm(blankForm);
      invalidateSponsorsCache();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const checkExpirations = async () => {
    try {
      const { data } = await api.post("/admin/sponsors/check-expirations?dry_run=true");
      if (!data.count) {
        toast("No sponsors expiring within 90 days");
      } else {
        toast.success(
          `${data.count} would be emailed to ${data.recipients.join(", ")}: ${data.sponsors
            .map((s) => `${s.name} (${s.days_left}d)`)
            .join(", ")}`
        );
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-zinc-400 max-w-2xl">
          Paid sponsor logos shown on the public footer + sidebar spotlight. Higher weight = more spotlight odds.
          Admins are emailed 90 days before a sponsorship expires (billing happens offline).
        </p>
        <button
          data-testid="sponsor-check-expirations"
          onClick={checkExpirations}
          className="inline-flex items-center gap-2 overline border border-white/15 px-4 py-2.5 hover:border-ec-volt hover:text-ec-volt transition-colors"
        >
          <BellRing size={15} /> Check expirations (dry run)
        </button>
      </div>

      {loading ? (
        <p className="overline text-zinc-500">Loading…</p>
      ) : !items.length ? (
        <div className="border border-white/10 bg-ec-surface p-12 text-center">
          <p className="font-heading text-3xl text-zinc-400">No sponsors yet</p>
          <p className="text-zinc-500 mt-2">Add one below — it appears on the public site immediately.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="sponsors-list">
          {items.map((s) => (
            <SponsorCard key={s.id} sponsor={s} allCities={cities} onChange={() => { invalidateSponsorsCache(); load(); }} />
          ))}
        </div>
      )}

      <div>
        <h3 className="font-heading text-3xl tracking-wide mb-4 flex items-center gap-2">
          <Megaphone size={22} className="text-ec-volt" /> Add a Sponsor
        </h3>
        <form onSubmit={add} className="bg-ec-surface border border-white/10 p-6 space-y-4 max-w-2xl" data-testid="add-sponsor-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="overline text-zinc-500 mb-2 block">Name *</span>
              <input data-testid="sponsor-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="e.g. Sixways Motors" />
            </label>
            <label className="block">
              <span className="overline text-zinc-500 mb-2 block">Website link</span>
              <input data-testid="sponsor-link" type="url" value={form.link_url} onChange={set("link_url")} className={inputCls} placeholder="https://…" />
            </label>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="overline text-zinc-500 mb-2 block">Expires *</span>
              <input data-testid="sponsor-expires" type="date" value={form.expires_at} onChange={set("expires_at")} className={inputCls} />
            </label>
            <label className="block">
              <span className="overline text-zinc-500 mb-2 block">Display weight: {form.display_weight}</span>
              <input
                data-testid="sponsor-weight"
                type="range"
                min="1"
                max="10"
                value={form.display_weight}
                onChange={(e) => setForm({ ...form, display_weight: Number(e.target.value) })}
                className="w-full accent-ec-volt mt-3"
              />
            </label>
          </div>
          <label className="block">
            <span className="overline text-zinc-500 mb-2 block">Cities (none = all cities)</span>
            <CityPicker cities={cities} selected={form.cities} onChange={(c) => setForm({ ...form, cities: c })} testidPrefix="sponsor-add" />
          </label>
          <label className="block">
            <span className="overline text-zinc-500 mb-2 block">Logo (PNG/JPG) *</span>
            <input
              data-testid="sponsor-file"
              type="file"
              accept="image/*"
              onChange={(e) => setForm({ ...form, file: e.target.files[0] || null })}
              className="block w-full text-sm text-zinc-400 file:mr-4 file:border-0 file:bg-ec-volt file:text-black file:px-4 file:py-2 file:overline file:cursor-pointer"
            />
          </label>
          <button
            type="submit"
            disabled={saving}
            data-testid="sponsor-submit"
            className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-6 py-3 hover:-translate-y-0.5 transition-all disabled:opacity-50"
          >
            <Plus size={16} /> {saving ? "Adding…" : "Add Sponsor"}
          </button>
        </form>
      </div>
    </div>
  );
};

function SponsorCard({ sponsor, allCities = [], onChange }) {
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({
    name: sponsor.name,
    link_url: sponsor.link_url || "",
    expires_at: sponsor.expires_at,
    display_weight: sponsor.display_weight,
    cities: sponsor.cities || [],
  });

  const left = daysUntil(sponsor.expires_at);
  const expiringSoon = left < 90;
  const cityLabel = !sponsor.cities || sponsor.cities.length === 0 ? "All cities" : sponsor.cities.join(", ");

  const toggleActive = async () => {
    try {
      await api.patch(`/admin/sponsors/${sponsor.id}`, { active: !sponsor.active });
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/admin/sponsors/${sponsor.id}`, {
        name: edit.name,
        link_url: edit.link_url,
        expires_at: edit.expires_at,
        display_weight: Number(edit.display_weight),
        cities: edit.cities,
      });
      toast.success("Sponsor updated");
      setEditing(false);
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async () => {
    if (!window.confirm(`Delete sponsor "${sponsor.name}"?`)) return;
    try {
      await api.delete(`/admin/sponsors/${sponsor.id}`);
      toast.success("Sponsor deleted");
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid={`sponsor-row-${sponsor.id}`} className="bg-ec-surface border border-white/10 p-5">
      <div className="flex items-start gap-4">
        <div className="w-24 h-16 bg-black/40 border border-white/10 flex items-center justify-center shrink-0">
          <img src={sponsor.image_data_uri} alt={sponsor.name} className="max-h-14 max-w-[88px] object-contain" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-2xl tracking-wide leading-none truncate">{sponsor.name}</h4>
          {sponsor.link_url && (
            <a href={sponsor.link_url} target="_blank" rel="noreferrer" className="text-xs text-zinc-500 hover:text-ec-volt underline break-all">
              {sponsor.link_url}
            </a>
          )}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span data-testid={`sponsor-expiry-${sponsor.id}`} className={`overline ${expiringSoon ? "text-ec-red" : "text-zinc-500"}`}>
              Expires {sponsor.expires_at}{left >= 0 ? ` · ${left}d` : " · expired"}
            </span>
            <span className="overline text-zinc-500">Weight {sponsor.display_weight}</span>
            <span data-testid={`sponsor-cities-${sponsor.id}`} className="overline text-zinc-500">{cityLabel}</span>
          </div>
        </div>
      </div>

      {editing && (
        <div className="mt-4 space-y-3 border-t border-white/10 pt-4" data-testid={`sponsor-edit-${sponsor.id}`}>
          <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={inputCls} placeholder="Name" />
          <input value={edit.link_url} onChange={(e) => setEdit({ ...edit, link_url: e.target.value })} className={inputCls} placeholder="https://…" />
          <div className="grid grid-cols-2 gap-3">
            <input type="date" value={edit.expires_at} onChange={(e) => setEdit({ ...edit, expires_at: e.target.value })} className={inputCls} />
            <label className="block">
              <span className="text-xs text-zinc-500">Weight {edit.display_weight}</span>
              <input type="range" min="1" max="10" value={edit.display_weight} onChange={(e) => setEdit({ ...edit, display_weight: e.target.value })} className="w-full accent-ec-volt" />
            </label>
          </div>
          <CityPicker cities={allCities} selected={edit.cities} onChange={(c) => setEdit({ ...edit, cities: c })} testidPrefix={`sponsor-edit-${sponsor.id}`} />
          <div className="flex gap-2">
            <button data-testid={`sponsor-save-${sponsor.id}`} onClick={saveEdit} className="inline-flex items-center gap-1.5 overline bg-ec-volt text-black px-4 py-2"><Check size={14} /> Save</button>
            <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1.5 overline border border-white/15 px-4 py-2 text-zinc-300"><X size={14} /> Cancel</button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
        <button
          data-testid={`sponsor-toggle-${sponsor.id}`}
          onClick={toggleActive}
          className={`overline px-3 py-1.5 border transition-colors ${sponsor.active ? "bg-ec-volt text-black border-ec-volt" : "border-white/15 text-zinc-500"}`}
        >
          {sponsor.active ? "LIVE" : "PAUSED"}
        </button>
        <button data-testid={`sponsor-edit-btn-${sponsor.id}`} onClick={() => setEditing((v) => !v)} className="ml-auto text-zinc-500 hover:text-ec-volt transition-colors"><Pencil size={16} /></button>
        <button data-testid={`sponsor-delete-${sponsor.id}`} onClick={del} className="text-zinc-500 hover:text-ec-red transition-colors"><Trash2 size={16} /></button>
      </div>
    </div>
  );
}
